import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import type { RecentTransaction } from "../types/wallets-api.types";
import { mapRuntimeTransaction } from "../utils/mapRuntimeTransaction";
import { useOperationLabels } from "./useOperationLabels";
import { useWalletEntryLabels } from "./useWalletEntryLabels";

const RECENT_LIMIT = 3;

export interface UseRuntimeWalletActivityResult {
  activityByWallet: Record<string, RecentTransaction[]>;
  isLoadingActivity: boolean;
  /** Re-reads ONE wallet's recent legs — no batch, no page-level spinner. */
  refreshOne: (walletId: string) => Promise<void>;
}

/**
 * Recent activity for the home cards: the latest legs of every wallet's ledger statement in ONE request (`GET
 * /wallets/recent-transactions`). Re-runs when the wallet set changes (e.g. after a refresh or a new
 * activation), so it stays fresh without polling.
 */
export const useRuntimeWalletActivity = (wallets: RuntimeWallet[]): UseRuntimeWalletActivityResult => {
  const operationLabels = useOperationLabels();
  const { describe, statusOf } = useWalletEntryLabels();
  const [activityByWallet, setActivityByWallet] = useState<Record<string, RecentTransaction[]>>({});
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  // The latest issued batch wins — a stale slow response must never overwrite a newer one.
  const requestSeq = useRef(0);

  // Depend on the set of ids (stable identity per wallets state), not on balances — avoids refetch churn.
  // Keyed by CONTENT, not by array identity: a live balance push replaces the wallets array on every
  // credit, and depending on its identity would re-fetch the recent activity of every wallet each time.
  const walletIdsKey = wallets.map((w) => w.id).join(",");
  const walletIds = useMemo(() => (walletIdsKey ? walletIdsKey.split(",") : []), [walletIdsKey]);

  useEffect(() => {
    const seq = ++requestSeq.current;
    let cancelled = false;

    // All state updates live inside the async body so none run synchronously within the effect.
    void (async () => {
      if (walletIds.length === 0) {
        setActivityByWallet({});
        // Also clears a spinner left by an in-flight batch that this run superseded.
        setIsLoadingActivity(false);
        return;
      }
      setIsLoadingActivity(true);
      const map: Record<string, RecentTransaction[]> = {};
      try {
        const res = await PaymentsWalletsService.listRecentTransactions(walletIds, RECENT_LIMIT);
        for (const { wallet_id, transactions } of res.data.data) {
          map[wallet_id] = transactions.map((tx) =>
            mapRuntimeTransaction(tx, operationLabels, describe, statusOf),
          );
        }
      } catch {
        // Cards fall back to their empty state; each card's refresh still re-reads its own legs.
      }
      if (cancelled || seq !== requestSeq.current) return;
      setActivityByWallet(map);
      setIsLoadingActivity(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [walletIds, operationLabels, describe, statusOf]);

  /**
   * A card's refresh must show its OWN new movements. The batch above only re-runs when the set of wallet ids
   * changes, so without this the balance updated and the three legs under it stayed as they were.
   *
   * Leaves `isLoadingActivity` alone: that flag drives the skeleton on every card, and one card refreshing is
   * not a reason to blank the rest.
   */
  const refreshOne = useCallback(
    async (walletId: string) => {
      try {
        const res = await PaymentsWalletsService.listTransactions(walletId, { limit: RECENT_LIMIT });
        const rows = res.data.data.map((tx) =>
          mapRuntimeTransaction(tx, operationLabels, describe, statusOf),
        );
        setActivityByWallet((current) => ({ ...current, [walletId]: rows }));
      } catch {
        // Keep whatever is on screen — a failed refresh must not empty a list that was correct.
      }
    },
    [operationLabels, describe, statusOf],
  );

  return { activityByWallet, isLoadingActivity, refreshOne };
};
